import { Activity, CalendarIcon, CheckCircle2, Cloud, CloudLightning, Download, Globe, HexagonIcon, Layers, Search, Server, Shield, X, XCircle } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../utils/utils";
import { Calendar } from "../../ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { Device } from "../interfaces/EndpointInterfaces";
import { format } from "date-fns";
import OnPremResourceRequests from "./Tabs/OnPremResourceRequests";
import { WebUrlRequestDetails } from "./Tabs/WebUrlRequestDetails";
import { AzureUserRequestDetails } from "./Tabs/AzureUserRequestDetails";
import { GCPRequestDetails } from "./Tabs/GCPRequestDetails";
import { K8RequestDetails } from "./Tabs/K8RequestDetails";
import { downloadOnPremRequestReport } from "./Api/resourcerequestapi";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import AWSResourceRequests from "./Tabs/AWSResourceRequests";
import { FaGoogle } from "react-icons/fa6";
import { SiAmazon, SiKubernetes } from "react-icons/si";
import { VscAzure } from "react-icons/vsc";



type PlatformType = "onprem" | "aws" | "azure" | "gcp" | "k8s" | "weburl";

interface PlatformConfig {
  id: PlatformType;
  name: string;
  icon: any;
  color: string;
  description: string;
}

const platformConfigs: PlatformConfig[] = [
  {
    id: "onprem",
    name: "Endpoints",
    icon: Server,
    color: "text-slate-600",
    description: "On-premises infrastructure"
  },
  {
    id: "aws",
    name: "AWS",
    icon: SiAmazon,
    color: "text-orange-600",
    description: "Amazon Web Services"
  },
  {
    id: "azure",
    name: "Azure",
    icon: VscAzure,
    color: "text-blue-600",
    description: "Microsoft Azure"
  },
  {
    id: "gcp",
    name: "GCP",
    icon: FaGoogle,
    color: "text-green-600",
    description: "Google Cloud Platform"
  },
  {
    id: "k8s",
    name: "K8s",
    icon: SiKubernetes,
    color: "text-purple-600",
    description: "Kubernetes clusters"
  },
  {
    id: "weburl",
    name: "WebURL",
    icon: Globe,
    color: "text-indigo-600",
    description: "Web applications"
  }
];

const mockRequestCounts = {
  onprem: { all: 8, pending: 0, approved: 0, declined: 0, revoked: 0 },
  aws: { all: 3, pending: 1, approved: 1, declined: 0, revoked: 1 },
  azure: { all: 7, pending: 0, approved: 0, declined: 1, revoked: 0 },
  gcp: { all: 2, pending: 0, approved: 0, declined: 0, revoked: 0 },
  k8s: { all: 5, pending: 1, approved: 0, declined: 0, revoked: 1 },
  weburl: { all: 7, pending: 1, approved: 0, declined: 0, revoked: 1 }
};


export default function ResourceRequestDetails() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activePlatform, setActivePlatform] = useState<PlatformType>("onprem");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const { toast } = useToast();
  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
  const { email, role, userRole } = useUserStore((state) => state.user);



  const handleReset = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setStatusFilter("all")
    setSearchTerm("")
  }

  // Reset selections when filters or platform change
  useEffect(() => {
    setSelectedRequests([]);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, priorityFilter, activePlatform]);


  const deferredSearchTerm = useDeferredValue(searchTerm);

  const currentPlatformConfig = useMemo(() => {
    return platformConfigs.find(p => p.id === activePlatform) || platformConfigs[0];
  }, [activePlatform]);


  const handleDownloadReport = async () => {
    toast({
      title: "Download Started",
      description: `${currentPlatformConfig.name} resource requests report is being downloaded...`,
    });
    await downloadOnPremRequestReport(activePlatform,
      tenantName,
      "",
      role,
      startDate ? startDate.toISOString() : "",
      endDate ? endDate.toISOString() : "",
      searchTerm,
      email,
      statusFilter
    );


  };
  const isResetDisabled =
    !searchTerm && !startDate && !endDate && (!statusFilter || statusFilter === "all");
  return (

    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-slate-900/50 dark:via-slate-800 dark:to-slate-900/80">
      <div className="p-6 border-b border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 text-white shadow-lg rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Resource Access Management</h2>
            </div>
          </div>

          {role !== "TENANT_USER" && <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadReport}
              data-testid="button-download-report"
            >
              <Download className="w-4 h-4 mr-1" />
              Download Report
            </Button>
          </div>}

        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative max-w-xs">
            <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-slate-400" />
            <Input
              placeholder={`Search requests...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-200 dark:border-slate-600"
              data-testid="input-search"
            />
          </div>

          {/* Start Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-32 justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
                data-testid="button-start-date"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {startDate ? format(startDate, "MMM dd") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  setStartDate(date);
                  setEndDate(undefined);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* End Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-32 justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
                data-testid="button-end-date"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {endDate ? format(endDate, "MMM dd") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                initialFocus
                disabled={(date) => {
                  if (!startDate) return true;
                  return date <= startDate
                }}
              />
            </PopoverContent>
          </Popover>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="DECLINE">Declined</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={isResetDisabled}
            className={cn(
              "justify-start text-left font-normal transition-colors gap-1.5 px-3 py-2",
              isResetDisabled && "opacity-50 cursor-not-allowed"
            )}
            data-testid="button-reset-search"
          >
            <X className="w-2 h-4" />
            Reset
          </Button>

        </div>
      </div>

      {/* Platform Tabs */}

      <div className="px-6 pt-4">
        <Tabs value={activePlatform} onValueChange={(value) => setActivePlatform(value as PlatformType)} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            {platformConfigs.map((platform) => {
              const IconComponent = platform.icon;
              const count = mockRequestCounts[platform.id]?.all || 0;
              return (
                <TabsTrigger
                  key={platform.id}
                  value={platform.id}
                  className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100 data-[state=active]:border-b-2 data-[state=active]:border-blue-500 font-medium"
                >
                  <IconComponent className={`w-4 h-4 ${platform.color}`} />
                  {platform.name}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      {/* Platform Content */}
      <div className="flex-1 p-6 overflow-y-auto">

        <Tabs value={activePlatform} onValueChange={(value) => setActivePlatform(value as PlatformType)} className="w-full h-full">
          <TabsContent value="onprem" className="h-full mt-0">
            <OnPremResourceRequests
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>
          <TabsContent value="aws" className="h-full mt-0">
            <AWSResourceRequests
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>

          <TabsContent value="azure" className="h-full mt-0">
            <AzureUserRequestDetails
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>
          <TabsContent value="gcp" className="h-full mt-0">
            <GCPRequestDetails
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>

          <TabsContent value="k8s" className="h-full mt-0">
            <K8RequestDetails
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>


          <TabsContent value="weburl" className="h-full mt-0">
            <WebUrlRequestDetails
              computerName={""}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              startDate={startDate}
              endDate={endDate}
              activeTab={activePlatform}
            />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  )
}